import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Package, TrendingUp, Copy, Key, Pencil, Trash2, Calendar } from 'lucide-react';

const ToolCard = ({ 
  tool, 
  user, 
  onAdjustStock, 
  onCopy, 
  onAddToRental, 
  onEdit, 
  onDelete,
  formatDate,
  isExpired,
  isExpiringSoon 
}) => {
  const navigate = useNavigate();

  return (
    <Card
      data-testid={`tool-card-${tool.id}`}
      className="hover:shadow-lg transition-shadow duration-200 overflow-hidden cursor-pointer"
      onClick={() => navigate(`/tools/${tool.id}`)}
    >
      {tool.photo ? (
        <div className="w-full h-48 bg-muted/30 flex items-center justify-center p-2">
          <img
            src={tool.photo}
            alt={tool.name}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      ) : (
        <div className="w-full h-48 bg-muted flex items-center justify-center">
          <Package className="w-12 h-12 text-muted-foreground" />
        </div>
      )}
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg font-heading">{tool.name}</CardTitle>
            <p className="text-sm text-muted-foreground">{tool.type}</p>
          </div>
          {tool.is_in_rental && (
            <Badge variant="outline" className="border-blue-500 text-blue-600 bg-blue-50 text-xs">
              <Key className="w-3 h-3 mr-1" />
              Location
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-xs">
          <span className="text-muted-foreground">Article:</span> {tool.article_number}
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Stock total:</span>
            <Badge variant="outline">{tool.quantity_total}</Badge>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Disponibles:</span>
            <Badge variant="default" className="bg-chart-3">{tool.quantity_available}</Badge>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Affectés:</span>
            <Badge variant="secondary">{tool.quantity_assigned}</Badge>
          </div>
        </div>

        {tool.order_number && (
          <div className="text-xs pt-2 border-t">
            <span className="text-muted-foreground">Commande:</span> {tool.order_number}
          </div>
        )}

        {tool.expiration_date && (
          <div className={`flex items-center gap-2 text-xs pt-2 ${!tool.order_number ? 'border-t' : ''}`}>
            <Calendar className="w-3 h-3" />
            <span className="text-muted-foreground">Expire:</span>
            <span className={isExpired(tool.expiration_date) ? 'text-destructive font-medium' : isExpiringSoon(tool.expiration_date) ? 'text-orange-500 font-medium' : ''}>
              {formatDate(tool.expiration_date)}
            </span>
            {isExpired(tool.expiration_date) && (
              <Badge variant="destructive" className="text-[10px] px-1 py-0">Expiré</Badge>
            )}
            {isExpiringSoon(tool.expiration_date) && !isExpired(tool.expiration_date) && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 border-orange-500 text-orange-500">Bientôt</Badge>
            )}
          </div>
        )}

        {user?.role === 'admin' && (
          <div className="flex gap-2 pt-2 flex-wrap">
            <Button
              data-testid={`adjust-stock-${tool.id}`}
              onClick={(e) => onAdjustStock(tool, e)}
              variant="outline"
              size="sm"
              className="flex-1 rounded-full"
            >
              <TrendingUp className="w-3 h-3 mr-1" />
              Stock
            </Button>
            <Button
              data-testid={`copy-tool-${tool.id}`}
              onClick={(e) => onCopy(tool, e)}
              variant="outline"
              size="sm"
              className="rounded-full"
              title="Copier l'outil"
            >
              <Copy className="w-3 h-3" />
            </Button>
            <Button
              data-testid={`add-to-rental-${tool.id}`}
              onClick={(e) => onAddToRental(tool, e)}
              variant="outline"
              size="sm"
              className="rounded-full"
              title="Ajouter à la location"
            >
              <Key className="w-3 h-3" />
            </Button>
            <Button
              data-testid={`edit-tool-${tool.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(tool);
              }}
              variant="outline"
              size="sm"
              className="rounded-full"
            >
              <Pencil className="w-3 h-3" />
            </Button>
            <Button
              data-testid={`delete-tool-${tool.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(tool.id);
              }}
              variant="destructive"
              size="sm"
              className="rounded-full"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ToolCard;
